// Consola del gateway de IA y MCP de Glubi: un solo lugar por negocio para sus conexiones a modelos, a su POS, a
// sus sistemas internos y externos y a sus credenciales. La usan el administrador y los implementadores que lo
// atienden; un implementador cambia de negocio desde el menú. También es donde viven sus agentes y rutinas.
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, RouterProvider } from '@tanstack/react-router';
import '@glubi/ds/estilos.css';
import './estilos.css';
import { Cargando, Icon } from '@glubi/ds';
import { SesionProvider, useSesion } from './lib/sesion';
import { Marco } from './componentes/Marco';
import { Entrar } from './pantallas/Entrar';
import { Resumen } from './pantallas/Resumen';
import { Modelos } from './pantallas/Modelos';
import { Conectores } from './pantallas/Conectores';
import { Llaves } from './pantallas/Llaves';
import { Credenciales } from './pantallas/Credenciales';
import { Registro } from './pantallas/Registro';
import { Probador } from './pantallas/Probador';
import { Negocios } from './pantallas/Negocios';
import { Tablero } from './pantallas/Tablero';
import { Agentes } from './pantallas/Agentes';
import { Rutinas } from './pantallas/Rutinas';

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: true } } });

function Protegida() {
  const { sesion, listo, yo, cuenta, toast } = useSesion();
  if (!listo) return <Cargando />;
  if (!sesion) return <Entrar />;
  if (!yo) return <Cargando texto="Abriendo el gateway" />;
  if (!cuenta) {
    return <Entrar aviso={`${yo.usuario.correo ?? 'Esa cuenta'} no administra ni implementa ningún negocio en Glubi. El gateway es de quien arma las conexiones del negocio.`} />;
  }
  return (
    <>
      <Marco><Outlet /></Marco>
      {toast && <div className="toast" role="status" key={toast.id}><Icon name="check" size={16} color="var(--amber)" />{toast.texto}</div>}
    </>
  );
}

const raiz = createRootRoute({ component: () => <Outlet /> });
const app = createRoute({ getParentRoute: () => raiz, id: 'consola', component: Protegida });
const pantalla = (path: string, component: () => React.ReactNode) => createRoute({ getParentRoute: () => app, path, component });

const arbol = raiz.addChildren([
  app.addChildren([
    pantalla('/', () => <Navigate to="/resumen" />),
    pantalla('/resumen', Resumen),
    pantalla('/modelos', Modelos),
    pantalla('/conectores', Conectores),
    pantalla('/llaves', Llaves),
    pantalla('/credenciales', Credenciales),
    pantalla('/registro', Registro),
    pantalla('/probador', Probador),
    pantalla('/negocios', Negocios),
    pantalla('/tablero', Tablero),
    pantalla('/agentes', Agentes),
    pantalla('/rutinas', Rutinas),
  ]),
]);

const router = createRouter({ routeTree: arbol, defaultPreload: 'intent' });

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <SesionProvider>
        <RouterProvider router={router} />
      </SesionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
